import { EKIDEN_API_URL } from "@constants";
import { FAUCET_APT } from "@constants/tokens";

import type { Market } from "@src/api";
import { type TokenMetadata, getMultipleTokenMetadata } from "@src/api/aptos";
import { getEkidenSdkClient } from "@src/api/sdk";
import type { InstrumentInfo, InstrumentsInfoResponse } from "@src/api/types";

export interface MarketData {
  fundingRate?: number;
  nextFundingTime?: Date;
  oraclePrice?: number;
  volume24h?: number;
  change24h?: number;
  high24h?: number;
  low24h?: number;
}

export interface LoadMarketsResult {
  metadata: Record<string, TokenMetadata | null>;
}

export interface MarketStatsResult {
  symbol: string;
  volume24h: number;
  change24h?: number;
  high24h?: number;
  low24h?: number;
}

export interface TickerData {
  symbol: string;
  addr: string;
  funding_rate: string;
  next_funding_time: number;
  open_interest_value: string;
}

export class MarketService {
  loadTickers = async (): Promise<TickerData[]> => {
    const response = await getEkidenSdkClient().market.getTickers();
    return response.list.map(
      (ticker: {
        symbol: string;
        addr: string;
        funding_rate: string;
        next_funding_time: string;
        open_interest_value: string;
      }) => ({
        symbol: ticker.symbol,
        addr: ticker.addr,
        funding_rate: ticker.funding_rate,
        next_funding_time: Number(ticker.next_funding_time),
        open_interest_value: ticker.open_interest_value,
      }),
    );
  };

  loadMarketsData = async (markets: Market[]): Promise<LoadMarketsResult> => {
    const tokens = markets.flatMap((market) => [market.quote_addr]);
    tokens.push(FAUCET_APT);

    const metadata = await getMultipleTokenMetadata(tokens);

    return { metadata };
  };

  loadMarketStats = async (symbol: string): Promise<MarketStatsResult> => {
    const response = await getEkidenSdkClient().market.getMarketStats(symbol);

    return {
      symbol,
      volume24h: response.turnover_24h,
      change24h: response.price_change_24h,
      high24h: response.high_24h,
      low24h: response.low_24h,
    };
  };

  loadFullMarketData = async (symbol: string): Promise<MarketData> => {
    const statsResult = await this.loadMarketStats(symbol);

    return {
      volume24h: statsResult.volume24h,
      change24h: statsResult.change24h,
      high24h: statsResult.high24h,
      low24h: statsResult.low24h,
    };
  };

  loadInstrumentsInfo = async (): Promise<InstrumentInfo[]> => {
    const response = await fetch(`${EKIDEN_API_URL}/market/instruments-info`);

    if (!response.ok) {
      return [];
    }

    const data: InstrumentsInfoResponse = await response.json();
    return data.list;
  };
}

export const marketService = new MarketService();
